'use client';

import React from 'react';
import Link from 'next/link';
import { CSSVars } from '@/styles/styles.config';
import { IScenario, IMetricStat } from './useCaseData';
import { clampExcerpt } from '@/helpers/excerpt';
import {
  StyledSection,
  StyledInner,
  StyledSectionLabel,
  StyledSectionHeading,
  StyledSectionIntro,
  StyledStepsGrid,
  StyledStepCard,
  StyledScenariosGrid,
  StyledScenarioCard,
  StyledDataTable,
  StyledStatGrid,
  StyledStatCard,
  StyledIntegrationSteps,
  StyledArticlesGrid,
  StyledArticleCard,
} from './UseCaseContent.styles';

interface IWidgetGuideRow {
  name: string;
  slug: string;
  bestFor: string;
  keyFeature: string;
}

interface IRelatedPost {
  _id?: string;
  name: string;
  slug: string;
  excerpt: string;
}

interface IProps {
  audienceName: string;
  primaryPlatform: string;
  scenarios: IScenario[];
  metrics: IMetricStat[];
  widgetGuide: IWidgetGuideRow[];
  widgetGuideIntro: string;
  integrationSteps: string[];
  relatedPosts: IRelatedPost[];
}

export const UseCaseContent = ({
  audienceName,
  primaryPlatform,
  scenarios,
  metrics,
  widgetGuide,
  widgetGuideIntro,
  integrationSteps,
  relatedPosts,
}: IProps) => {
  return (
    <>
      {/* How It Works */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>HOW IT WORKS</StyledSectionLabel>
          <StyledSectionHeading>
            Get started in 3 simple steps
          </StyledSectionHeading>
          <StyledSectionIntro>
            No account needed to explore. Pick a widget, customize it, and embed
            it on your site in under a minute.
          </StyledSectionIntro>
          <StyledStepsGrid>
            <StyledStepCard>
              <span className="step-number">1</span>
              <h3>Browse widgets</h3>
              <p>
                Explore <Link href="/widgets">200+ widgets</Link> organized by
                category, or search for exactly what you need. Every widget has
                a live preview.
              </p>
            </StyledStepCard>
            <StyledStepCard>
              <span className="step-number">2</span>
              <h3>Customize</h3>
              <p>
                Use the visual editor to match your brand colors, fonts, and
                layout. No coding needed, just point and click.
              </p>
            </StyledStepCard>
            <StyledStepCard>
              <span className="step-number">3</span>
              <h3>Embed</h3>
              <p>
                Copy the embed code and paste it into {primaryPlatform} or any
                other platform. Your widget is live in seconds.
              </p>
            </StyledStepCard>
          </StyledStepsGrid>
        </StyledInner>
      </StyledSection>

      {/* Scenarios */}
      <StyledSection $bg={CSSVars.gray50}>
        <StyledInner>
          <StyledSectionLabel>USE CASES</StyledSectionLabel>
          <StyledSectionHeading>
            How {audienceName} use Common Ninja
          </StyledSectionHeading>
          <StyledScenariosGrid>
            {scenarios.map((s, i) => (
              <StyledScenarioCard key={i}>
                <h3>{s.title}</h3>
                <p>{s.description}</p>
                <div className="scenario-widgets">Widgets: {s.widgets}</div>
                <div className="scenario-outcome">Outcome: {s.outcome}</div>
              </StyledScenarioCard>
            ))}
          </StyledScenariosGrid>
        </StyledInner>
      </StyledSection>

      {/* Widget Guide Table */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>WIDGET GUIDE</StyledSectionLabel>
          <StyledSectionHeading>
            Which widgets are right for you?
          </StyledSectionHeading>
          <StyledSectionIntro>{widgetGuideIntro}</StyledSectionIntro>
          <StyledDataTable>
            <table>
              <thead>
                <tr>
                  <th>Widget</th>
                  <th>Best For</th>
                  <th>Key Feature</th>
                  <th>Try It</th>
                </tr>
              </thead>
              <tbody>
                {widgetGuide.map((row) => (
                  <tr key={row.slug}>
                    <td className="col-name">{row.name}</td>
                    <td>{row.bestFor}</td>
                    <td>{row.keyFeature}</td>
                    <td className="col-link">
                      <Link href={`/widgets/${row.slug}`}>Try Free</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </StyledDataTable>
        </StyledInner>
      </StyledSection>

      {/* Success Metrics */}
      <StyledSection $bg={CSSVars.gray50}>
        <StyledInner>
          <StyledSectionLabel>RESULTS</StyledSectionLabel>
          <StyledSectionHeading>
            What {audienceName} achieve with Common Ninja
          </StyledSectionHeading>
          <StyledStatGrid>
            {metrics.map((m, i) => (
              <StyledStatCard key={i}>
                <div className="stat-value">{m.value}</div>
                <div className="stat-label">{m.label}</div>
                <p>{m.description}</p>
              </StyledStatCard>
            ))}
          </StyledStatGrid>
        </StyledInner>
      </StyledSection>

      {/* Integration Guide */}
      <StyledSection>
        <StyledInner>
          <StyledSectionLabel>INTEGRATIONS</StyledSectionLabel>
          <StyledSectionHeading>
            How to add widgets to {primaryPlatform}
          </StyledSectionHeading>
          <StyledSectionIntro>
            The same process works on 200+ platforms. Here is how to get started
            on {primaryPlatform}.
          </StyledSectionIntro>
          <StyledIntegrationSteps>
            {integrationSteps.map((step, i) => (
              <li key={i}>
                <span>{step}</span>
              </li>
            ))}
          </StyledIntegrationSteps>
        </StyledInner>
      </StyledSection>

      {/* Related Articles */}
      {relatedPosts.length > 0 && (
        <StyledSection $bg={CSSVars.gray50}>
          <StyledInner>
            <StyledSectionLabel>FROM THE BLOG</StyledSectionLabel>
            <StyledSectionHeading>
              Learn more about growing with widgets
            </StyledSectionHeading>
            <StyledArticlesGrid>
              {relatedPosts.map((post) => (
                <StyledArticleCard
                  key={post.slug}
                  as={Link}
                  href={`/blog/${post.slug}`}
                >
                  <h3>{post.name}</h3>
                  <p>
                    {clampExcerpt(post.excerpt)}
                  </p>
                  <span className="read-more">Read article</span>
                </StyledArticleCard>
              ))}
            </StyledArticlesGrid>
          </StyledInner>
        </StyledSection>
      )}
    </>
  );
};
